export type FilterDataType = 'text' | 'number' | 'date' | 'boolean';

export const EMPTY_FILTER_VALUE = '__NULL__';

export type OperatorTarget = 'single' | 'multiple';

export type FilterOperator =
  | 'is'
  | 'is any of'
  | 'is not'
  | 'not any of'
  | 'is before'
  | 'is after'
  | 'is greater than'
  | 'is greater than or equal to'
  | 'is less than'
  | 'is less than or equal to'
  | 'is between';

export type OperatorDetail = {
  value: FilterOperator;
  label: string;
  target: OperatorTarget;
  pluralForm?: FilterOperator;
  singularForm?: FilterOperator;
};

const TEXT_OPERATORS: OperatorDetail[] = [
  { value: 'is', label: 'is', target: 'single', pluralForm: 'is any of' },
  { value: 'is any of', label: 'is any of', target: 'multiple', singularForm: 'is' },
  { value: 'is not', label: 'is not', target: 'single', pluralForm: 'not any of' },
  { value: 'not any of', label: 'not any of', target: 'multiple', singularForm: 'is not' },
];

const NUMBER_OPERATORS: OperatorDetail[] = [
  { value: 'is', label: 'is', target: 'single', pluralForm: 'is between' },
  { value: 'is not', label: 'is not', target: 'single' },
  { value: 'is greater than', label: 'is greater than', target: 'single', pluralForm: 'is between' },
  { value: 'is greater than or equal to', label: 'is greater than or equal to', target: 'single', pluralForm: 'is between' },
  { value: 'is less than', label: 'is less than', target: 'single', pluralForm: 'is between' },
  { value: 'is less than or equal to', label: 'is less than or equal to', target: 'single', pluralForm: 'is between' },
  { value: 'is between', label: 'is between', target: 'multiple', singularForm: 'is' },
];

const DATE_OPERATORS: OperatorDetail[] = [
  { value: 'is', label: 'is', target: 'single', pluralForm: 'is between' },
  { value: 'is not', label: 'is not', target: 'single' },
  { value: 'is before', label: 'is before', target: 'single', pluralForm: 'is between' },
  { value: 'is after', label: 'is after', target: 'single', pluralForm: 'is between' },
  { value: 'is between', label: 'is between', target: 'multiple', singularForm: 'is' },
];

const BOOLEAN_OPERATORS: OperatorDetail[] = [
  { value: 'is', label: 'is', target: 'single' },
  { value: 'is not', label: 'is not', target: 'single' },
];

/** The fixed set of states a boolean column can be in; used to invert "is not". */
export const BOOLEAN_UNIVERSE = ['true', 'false', EMPTY_FILTER_VALUE] as const;

const OPERATORS_BY_DATA_TYPE: Record<FilterDataType, OperatorDetail[]> = {
  text: TEXT_OPERATORS,
  number: NUMBER_OPERATORS,
  date: DATE_OPERATORS,
  boolean: BOOLEAN_OPERATORS,
};

export function toFilterDataType(cellDataType?: string | boolean): FilterDataType {
  if (cellDataType === true || cellDataType === 'boolean') return 'boolean';
  if (cellDataType === 'number') return 'number';
  if (cellDataType === 'date' || cellDataType === 'dateString' || cellDataType === 'dateTime' || cellDataType === 'dateTimeString') return 'date';
  return 'text';
}

export function getOperators(dataType: FilterDataType): OperatorDetail[] {
  return OPERATORS_BY_DATA_TYPE[dataType];
}

/** Operators shown in the chip/panel menu. Text only offers polarity; plural wording is value-count driven. */
export function getMenuOperators(dataType: FilterDataType): OperatorDetail[] {
  if (dataType !== 'text') return getOperators(dataType);
  return getOperators(dataType).filter((detail) => !detail.singularForm);
}

export function getOperatorDetail(dataType: FilterDataType, operator: FilterOperator): OperatorDetail {
  const operators = getOperators(dataType);
  return operators.find((detail) => detail.value === operator) ?? operators[0];
}

export function getDefaultOperator(dataType: FilterDataType): FilterOperator {
  return getOperators(dataType)[0].value;
}

export function menuOperatorMatches(dataType: FilterDataType, current: FilterOperator, menuOperator: FilterOperator): boolean {
  if (current === menuOperator) return true;
  if (dataType !== 'text') return false;
  return getOperatorDetail(dataType, menuOperator).pluralForm === current;
}

export function textOperatorForValues(negative: boolean, valueCount: number): FilterOperator {
  if (negative) return valueCount > 1 ? 'not any of' : 'is not';
  return valueCount > 1 ? 'is any of' : 'is';
}

export function isNegationOperator(operator: FilterOperator): boolean {
  return operator === 'is not' || operator === 'not any of';
}

// Text offers is / is not (plural forms are value-count driven). Number and date keep full menus; boolean is / is not.
export function hasOperatorMenu(dataType: FilterDataType): boolean {
  return dataType === 'number' || dataType === 'date' || dataType === 'boolean' || dataType === 'text';
}

export function operatorValueCount(dataType: FilterDataType, operator: FilterOperator): number | 'many' {
  if (dataType === 'text') return operator === 'is any of' || operator === 'not any of' ? 'many' : 1;
  if (dataType === 'boolean') return 1;
  return getOperatorDetail(dataType, operator).target === 'multiple' ? 2 : 1;
}

/**
 * Promotes or demotes an operator when the number of selected values crosses the
 * boundary between one and many, so "is" becomes "is any of" on the second selection.
 */
export function determineNewOperator(dataType: FilterDataType, previousCount: number, nextCount: number, current: FilterOperator): FilterOperator {
  if (previousCount === nextCount) return current;
  if (previousCount >= 2 && nextCount >= 2) return current;
  if (previousCount <= 1 && nextCount <= 1) return current;

  const detail = getOperatorDetail(dataType, current);

  if (nextCount >= 2) return detail.pluralForm ?? current;
  return detail.singularForm ?? current;
}

/**
 * Boolean filters cycle in place instead of opening a list: Yes → No → Empty → Yes.
 * Empty keeps the filter and matches null / unset cells via EMPTY_FILTER_VALUE.
 */
export function nextBooleanValues(values: string[]): string[] {
  if (values[0] === 'true') return ['false'];
  if (values[0] === 'false') return [EMPTY_FILTER_VALUE];
  return ['true'];
}

export function formatBooleanLabel(value: string): string {
  if (value === 'true') return 'Yes';
  if (value === 'false') return 'No';
  return 'Empty';
}

/**
 * Reshapes a value list to match what an operator needs, so switching an existing filter
 * between "is" and "is between" always leaves a complete, writable model.
 */
export function adjustValuesForOperator<T>(dataType: FilterDataType, operator: FilterOperator, values: T[], fallbackSecondValue?: T): T[] {
  const count = operatorValueCount(dataType, operator);

  if (count === 'many') return values;
  if (count === 1) return values.slice(0, 1);

  if (values.length >= 2) return values.slice(0, 2);
  if (!values.length) return values;

  const second = fallbackSecondValue ?? values[0];
  return [values[0], second];
}
